import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MuiButton from "@mui/material/Button";
import secureLocalStorage from "react-secure-storage";
import { Button } from "../../../components/Button";

import vectorIcon from "../../../assets/_VectorIcon_.svg";
import addCircleImage from "./../../../assets/_AddCircleIcon_.svg";
import usePermission from "../../../hooks/usePermission";
import { FeatureStatic } from "../../../@types/statics/feature.static";
import { IProjectStatus } from "../../../@types/project/project-status.type";
import { ProjectStatusService } from "../../../services/api/projectStatus/ProjectStatusService";
import { IKeyValue } from "../../../@types/key-value.type";
import { AgGridReact } from "ag-grid-react";
import { themeGrid } from "../../../themeGrid";
import { localeText } from "../../../localeTextFromGrid";
import {
  ColDef,
  FilterChangedEvent,
  GridReadyEvent,
  RowDragEvent,
} from "ag-grid-community";
import styles from "./styles.module.scss";

export const ProjectStatus: React.FC = () => {
  const [projectStatusItems, setProjectStatusItems] = useState<
    IProjectStatus[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact<any>>(null);

  const navigate = useNavigate();
  const permissions = usePermission(FeatureStatic.costCenter.id);

  const columns: ColDef[] = [
    {
      field: "Drag",
      headerName: "",
      rowDrag: true,
      filter: false,
      sortable: false,
      maxWidth: 30,
      flex: 1,
    },
    {
      field: "name",
      headerName: "Nome",
      minWidth: 200,
      flex: 2,
    },
    {
      field: "description",
      headerName: "Descrição",
      minWidth: 200,
      flex: 2,
    },
    {
      field: "isVisible",
      headerName: "Visível no Portifólio",
      minWidth: 120,
      flex: 1,
      cellStyle: { "justify-content": "center" },
    },
    {
      field: "isCompleted",
      headerName: "Finalizado",
      minWidth: 120,
      flex: 1,
      cellStyle: { "justify-content": "center" },
    }
  ];

  const getFromCache = (key: string) => {
    const data = secureLocalStorage.getItem(key);
    return data ? JSON.parse(data as string) : null;
  };

  const [cachedFilters, setCachedFilters] = useState(
    getFromCache("agGridFiltersProjectStatus")
  );
  const [cachedSort, setCachedSort] = useState(
    getFromCache("agGridSortProjectStatus")
  );

  const saveToCache = (key: string, data: any) => {
    secureLocalStorage.setItem(key, JSON.stringify(data));
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    const filters = params.api.getFilterModel(); // Obtém o modelo de filtro
    saveToCache("agGridFiltersProjectStatus", filters); // Salva no cache
  };

  const onSortChanged = () => {
    const colState = gridRef.current!.api.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });

    saveToCache("agGridSortProjectStatus", sortState);
  };

  const onGridReady = (params: GridReadyEvent) => {
    const gridApi = params.api;

    // Restaurar filtros
    if (cachedFilters) {
      gridApi.setFilterModel(cachedFilters);
    }

    // Restaurar ordenação
    if (cachedSort) {
      gridApi.applyColumnState({
        state: cachedSort,
        defaultState: { sort: null },
      });
    }
  };

  async function fetchAll() {
    try {
      setLoading(true);
      const projectStatuses = await ProjectStatusService.getAllProjectStatus();
      setProjectStatusItems(projectStatuses);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!permissions.canView()) {
      navigate("/unauthorized");
      return;
    }

    fetchAll();
  }, []);

  function navigateToUpdatePage(id: string) {
    navigate(`update/${id}`);
  }

  const onRowDragEnd = async (event: RowDragEvent) => {
    const newRowOrder: IProjectStatus[] = [];
    event.api.forEachNodeAfterFilterAndSort((node) => {
      newRowOrder.push(node.data);
    });

    setProjectStatusItems(newRowOrder);
    const reorderItems: IKeyValue[] = newRowOrder.map(
      (value: IProjectStatus, index: number) => {
        return {
          key: value.id,
          value: `${index}`,
        };
      }
    );
    await ProjectStatusService.reorderProjectStatus(reorderItems);
    await fetchAll();
  };

  return (
    <div className={styles.projectStatus}>
      <div className={styles.back}>
        <MuiButton
          className={styles.buttonMui}
          onClick={() => navigate("/configurations")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </MuiButton>
      </div>
      <div className={styles.titleAndBtnCreate}>
        <h1>Status de Projetos</h1>

        <div className={styles.btnsFilterAndCreate}>
          <Button
            iconUrl={addCircleImage}
            onClick={() => {
              navigate("/configurations/projectstatus/create");
            }}
            content="Novo Status de Projeto"
            className={styles.button}
          />
        </div>
      </div>

      <div className={`${styles.dataGridContainer}`}>
        <AgGridReact
          ref={gridRef}
          rowData={projectStatusItems}
          columnDefs={columns}
          theme={themeGrid}
          localeText={localeText}
          loading={loading}
          overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
          defaultColDef={{
            sortable: true,
            editable: false,
            filter: true,
            floatingFilter: true,
          }}
          suppressCellFocus={true}
          rowStyle={{ cursor: "pointer" }}
          onRowClicked={(row) => navigateToUpdatePage(row.data.id)}
          onFilterChanged={onFilterChanged}
          onSortChanged={onSortChanged}
          rowDragManaged={true}
          animateRows={true}
          onRowDragEnd={onRowDragEnd}
          onGridReady={onGridReady}
        />
      </div>
    </div>
  );
};
